import React, { JSX, useState } from "react";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../contexts/AuthContext";
import "./JoinBoardModal.css";

type JoinBoardModalProps = {
  open: boolean;
  onClose: (joined?: boolean) => void;
};

export default function JoinBoardModal({ open, onClose }: JoinBoardModalProps): JSX.Element | null {
  const { user } = useAuth();
  const [inviteCode, setInviteCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Reset form when modal opens
  React.useEffect(() => {
    if (open) {
      setInviteCode("");
      setError("");
    }
  }, [open]);

  if (!open) return null;

  const handleJoin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    setError("");

    try {
      // 1. Find the board by invite code
      const { data: board, error: findError } = await supabase
        .from('boards')
        .select('id')
        .eq('invite_code', inviteCode)
        .single();

      if (findError || !board) {
        throw new Error("Invalid invite code. Please check and try again.");
      }

      // 2. Check if already a member
      const { data: existingMember } = await supabase
        .from('board_members')
        .select('id')
        .eq('board_id', board.id)
        .eq('user_id', user.id)
        .single();

      if (existingMember) {
        throw new Error("You are already a member of this board!");
      }

      // 3. Add user to board
      const { error: joinError } = await supabase
        .from('board_members')
        .insert({
          board_id: board.id,
          user_id: user.id,
          role: 'member'
        });

      if (joinError) throw joinError;

      // Success
      setInviteCode("");
      onClose(true);
    } catch (err: any) {
      console.error("Join error:", err);
      setError(err.message || "Failed to join board");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="join-board-modal">
      <div className="overlay" onClick={() => onClose(false)}>
        <div className="modal-container" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2 className="modal-title">Join Existing Board</h2>
            <button className="modal-close-button" onClick={() => onClose(false)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleJoin}>
            <div className="form-group">
              <label className="label">Invite Code</label>
              <input
                className="input"
                placeholder="Enter the 10-character code"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value)}
                maxLength={10}
                required
              />
              {error && <p style={{ color: 'red', marginTop: '8px', fontSize: '14px' }}>{error}</p>}
            </div>

            <button
              type="submit"
              className="join-button"
              disabled={loading || inviteCode.length < 1}
            >
              {loading ? 'Joining...' : 'Join Board'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
